"use client";
import {
	Avatar,
	Button,
	Checkbox,
	CheckboxGroup,
	Heading,
	InputText,
	Modal,
	Select,
	SelectItem,
	Text,
	TextArea
} from "@pamperedchef/design-system";
import { useState } from "react";
import {
	GoogleReCaptchaProvider,
	useGoogleReCaptcha
} from "react-google-recaptcha-v3";
import * as yup from "yup";

import * as styles from "./ContactYourConsultant.module.scss";

const validationSchema = yup.object().shape({
	firstName: yup
		.string()
		.trim()
		.max(30, "First name cannot be more than 30 characters")
		.matches(
			/^[A-Za-zÀ-ÖØ-öø-ÿ,.'"\x20-]*$/,
			"First name contains invalid characters"
		)
		.required("First name is required"),
	lastName: yup
		.string()
		.trim()
		.max(30, "Last name cannot be more than 30 characters")
		.matches(
			/^[A-Za-zÀ-ÖØ-öø-ÿ,.'"\x20-]*$/,
			"Last name contains invalid characters"
		)
		.required("Last name is required"),
	email: yup
		.string()
		.email("Enter a valid email address")
		.required("Email is required"),
	phoneNumber: yup
		.string()
		.test("is-phone", "Phone number is invalid", (value) => {
			if (!value) return true;
			return /^\(\d{3}\) \d{3}-\d{4}$/.test(value);
		})
		.optional(),
	bestTimeToConnect: yup.string().optional(),
	message: yup
		.string()
		.max(300, "Message cannot be more than 300 characters")
		.matches(/^[^<>;]*$/, "Message contains invalid characters")
		.test(
			"no-sql-injection",
			"Message contains invalid characters",
			(value) => !value || !/(--|\/\*)/.test(value)
		)
		.test(
			"no-script",
			"Message contains invalid characters",
			(value) => !value || !/<script|javascript:/i.test(value)
		)
		.required("Message is required"),
	purchaseProducts: yup.boolean().optional(),
	startABusiness: yup.boolean().optional(),
	hostAShow: yup.boolean().optional(),
	recaptcha: yup
		.string()
		.nullable()
		.required("Please complete the reCAPTCHA validation.")
});

export interface ContactYourConsultantProps {
	consultant: {
		consultantNumber: string;
		image?: string;
		name: string;
		location?: string;
	};
	thankYouMessage: {
		title?: string;
		description?: string;
	};
	closeButtonLabel?: string;
	triggerElement?: React.ReactNode;
	isSubmitted?: boolean;
	siteKey: string;
	onSubmit?: (data: ContactFormValues) => void;
	onClose?: () => void;
	isOpen?: boolean;
}

type Errors = Record<string, string>;

const formatPhoneNumber = (value: string) => {
	const cleaned = value.replace(/\D/g, "");
	const match = cleaned.startsWith("1") ? cleaned.slice(1) : cleaned;
	const digits = match.slice(0, 10);

	if (digits.length < 4) {
		return digits;
	}

	if (digits.length < 7) {
		return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
	}

	return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export interface ContactFormValues {
	consultantNumber: string;
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber?: string;
	message: string;
	country: string;
	bestTimeToConnect?: string;
	purchaseProducts?: boolean;
	startABusiness?: boolean;
	hostAShow?: boolean;
	recaptcha: string | null;
	siteKey: string;
}

/**
 * ContactYourConsultant component
 */
export function ContactYourConsultant({
	consultant,
	closeButtonLabel = "Close",
	isSubmitted,
	thankYouMessage,
	triggerElement,
	siteKey,
	onSubmit,
	onClose,
	isOpen = false
}: ContactYourConsultantProps): JSX.Element {
	if (!siteKey) {
		return (
			<ContactYourConsultantForm
				consultant={consultant}
				closeButtonLabel={closeButtonLabel}
				isSubmitted={isSubmitted}
				thankYouMessage={thankYouMessage}
				triggerElement={triggerElement}
				siteKey={siteKey}
				onSubmit={onSubmit}
				onClose={onClose}
				isOpen={isOpen}
			/>
		);
	}

	return (
		<GoogleReCaptchaProvider
			reCaptchaKey={siteKey}
			scriptProps={{ async: true, defer: true }}
		>
			<ContactYourConsultantForm
				consultant={consultant}
				closeButtonLabel={closeButtonLabel}
				isSubmitted={isSubmitted}
				thankYouMessage={thankYouMessage}
				triggerElement={triggerElement}
				siteKey={siteKey}
				onSubmit={onSubmit}
				onClose={onClose}
				isOpen={isOpen}
			/>
		</GoogleReCaptchaProvider>
	);
}

function ContactYourConsultantForm({
	consultant,
	closeButtonLabel = "Close",
	isSubmitted,
	thankYouMessage,
	triggerElement,
	siteKey,
	onSubmit,
	onClose,
	isOpen = false
}: ContactYourConsultantProps): JSX.Element {
	const { executeRecaptcha } = useGoogleReCaptcha();
	const [formValues, setFormValues] = useState<
		Omit<ContactFormValues, "consultantNumber">
	>({
		firstName: "",
		lastName: "",
		email: "",
		phoneNumber: "",
		bestTimeToConnect: "",
		message: "",
		country: "US",
		purchaseProducts: false,
		startABusiness: false,
		hostAShow: false,
		recaptcha: null,
		siteKey
	});
	const [errors, setErrors] = useState<Errors>({});
	/**
	 * Change event for inputs with validation on change
	 * @param value
	 * @param name
	 */
	async function handleChange(
		value: string | boolean,
		name: string
	): Promise<void> {
		let nextValue = value;

		if (name === "phoneNumber" && typeof value === "string") {
			const prevValue = formValues.phoneNumber || "";
			if (value.length >= prevValue.length) {
				nextValue = formatPhoneNumber(value);
			}
		}

		setFormValues({
			...formValues,
			[name]: nextValue
		});

		try {
			await validationSchema.validateAt(name, {
				...formValues,
				[name]: nextValue
			});
			setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
		} catch (error) {
			setErrors((prevErrors) => ({
				...prevErrors,
				[name]: (error as yup.ValidationError).message
			}));
		}
	}

	/**
	 * Form submit with full validation
	 * @param e
	 */
	async function handleFormSubmit(e: React.FormEvent): Promise<void> {
		e.preventDefault();

		const recaptchaToken = await triggerRecaptcha();
		const formValuesWithCaptcha = {
			...formValues,
			recaptcha: recaptchaToken
		};

		try {
			const isFormValid = await validationSchema.validate(
				formValuesWithCaptcha,
				{
					abortEarly: false
				}
			);

			setErrors({});
			setFormValues(formValuesWithCaptcha);
			onSubmit?.({
				consultantNumber: consultant.consultantNumber,
				...isFormValid,
				country: formValues.country,
				siteKey
			});
		} catch (err) {
			const validationErrors: Errors = {};

			(err as yup.ValidationError)?.inner?.forEach((error) => {
				if (error.path) {
					validationErrors[error.path] = error.message;
				}
			});

			setErrors(validationErrors);
		}
	}

	async function triggerRecaptcha(): Promise<string | null> {
		if (!siteKey) {
			return null;
		}

		if (!executeRecaptcha) {
			setErrors((prevErrors) => ({
				...prevErrors,
				recaptcha: "reCAPTCHA is not ready yet. Please try again."
			}));
			return null;
		}

		try {
			const token = await executeRecaptcha("contact_consultant");
			if (token) {
				setErrors((prevErrors) => ({ ...prevErrors, recaptcha: "" }));
			}
			return token;
		} catch (error) {
			setErrors((prevErrors) => ({
				...prevErrors,
				recaptcha:
					(error as yup.ValidationError)?.message ||
					"Unable to validate reCAPTCHA. Please retry."
			}));
			return null;
		}
	}

	return (
		<Modal
			className={styles["modal"]}
			overlayClassName={styles["overlay"]}
			triggerElement={triggerElement}
			isOpen={isOpen}
			onClose={onClose}
			onClosePress={onClose}
			aria-label="Contact Your Consultant"
		>
			{isSubmitted ? (
				<div className={styles["thank-you"]}>
					<Heading tag="h2">{thankYouMessage?.title}</Heading>

					<Text
						className={styles["thank-you-description"]}
						styledAs="xsr"
					>
						{thankYouMessage?.description}
					</Text>

					<Button kind="primary" onClick={onClose}>
						Continue shopping
					</Button>
				</div>
			) : (
				<>
					<div className={styles["header"]}>
						<Heading tag="h2">Contact Your Consultant</Heading>

						<div className={styles["consultant"]}>
							{consultant?.image ? (
								<Avatar
									src={consultant.image}
									style={{ height: 90, width: 90 }}
								/>
							) : null}

							<div className={styles["consultant-info"]}>
								<Heading tag="h3">
									{consultant?.name || ""}
								</Heading>

								<Text styledAs="sml">
									{consultant?.location}
								</Text>
							</div>
						</div>
					</div>

					<form
						className={styles["form"]}
						onSubmit={handleFormSubmit}
					>
						<div className={styles["form-row"]}>
							<InputText
								label="First Name"
								name="firstName"
								isRequired
								placeholder="Enter first name"
								value={formValues.firstName}
								onChange={(value) =>
									handleChange(value, "firstName")
								}
								isInvalid={!!errors.firstName}
								errorMessage={errors.firstName}
							/>
							<InputText
								label="Last Name"
								name="lastName"
								isRequired
								placeholder="Enter last name"
								value={formValues.lastName}
								onChange={(value) =>
									handleChange(value, "lastName")
								}
								isInvalid={!!errors.lastName}
								errorMessage={errors.lastName}
							/>
						</div>
						<div className={styles["form-row"]}>
							<InputText
								label="Email Address"
								name="email"
								isRequired
								placeholder="example: johndoe@gmail.com"
								value={formValues.email}
								onChange={(value) =>
									handleChange(value, "email")
								}
								isInvalid={!!errors.email}
								errorMessage={errors.email}
							/>
							<InputText
								label="Phone Number (Optional)"
								name="phoneNumber"
								placeholder="(___) ___-____"
								value={formValues.phoneNumber}
								onChange={(value) =>
									handleChange(value, "phoneNumber")
								}
								isInvalid={!!errors.phoneNumber}
								errorMessage={errors.phoneNumber}
							/>
						</div>

						<div
							className={`${styles["form-row"]} ${styles["form-row--half"]}`}
						>
							<Select
								label="Best Time to Contact (Optional)"
								defaultSelectedKey={
									formValues.bestTimeToConnect
								}
								onSelectionChange={(value) =>
									handleChange(
										value as string,
										"bestTimeToConnect"
									)
								}
							>
								{/* TODO: Once design-system updates the SelectItem component, remove these suppressors */}
								<SelectItem key="">Select</SelectItem>
								{/* @ts-expect-error - 'label' property is required but not recognized by current types */}
								<SelectItem key="morning" label="Morning">
									Morning
								</SelectItem>
								{/* @ts-expect-error - 'label' property is required but not recognized by current types */}
								<SelectItem key="afternoon" label="Afternoon">
									Afternoon
								</SelectItem>
								{/* @ts-expect-error - 'label' property is required but not recognized by current types */}
								<SelectItem key="evening" label="Evening">
									Evening
								</SelectItem>
							</Select>
						</div>
						<div className={styles["form-row"]}>
							<CheckboxGroup
								className={styles["checkboxes"]}
								label="Contact Me About"
								description="Check all that apply."
								aria-label="Contact Me About"
							>
								<Checkbox
									value="startABusiness"
									isSelected={formValues.startABusiness}
									onChange={(isChecked) =>
										handleChange(
											isChecked,
											"startABusiness"
										)
									}
								>
									Becoming a Pampered Chef Consultant
								</Checkbox>
								<Checkbox
									value="hostAShow"
									isSelected={formValues.hostAShow}
									onChange={(isChecked) =>
										handleChange(isChecked, "hostAShow")
									}
								>
									Hosting a Cooking Show
								</Checkbox>
								<Checkbox
									value="purchaseProducts"
									isSelected={formValues.purchaseProducts}
									onChange={(isChecked) =>
										handleChange(
											isChecked,
											"purchaseProducts"
										)
									}
								>
									Attending a Cooking Show
								</Checkbox>
							</CheckboxGroup>
						</div>

						<div className={styles["form-row"]}>
							<TextArea
								label="Message"
								name="message"
								isRequired
								maxCount={300}
								placeholder="Enter your message"
								value={formValues.message}
								onChange={(value) =>
									handleChange(value, "message")
								}
								isInvalid={!!errors.message}
								errorMessage={errors.message}
							/>
						</div>

						{errors.recaptcha && (
							<Text styledAs="smr" className={styles["error"]}>
								{errors.recaptcha}
							</Text>
						)}

						<Button
							kind="primary"
							size="lg"
							className={styles["submit"]}
							type="submit"
						>
							Send Message
						</Button>
					</form>
				</>
			)}
		</Modal>
	);
}
